import { sharedShowApi } from '@/api/sharedShowApi'
import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { schedulerApi } from '@/api/schedulerApi'
import { customersApi } from '@/api/customersApi'
import type { Customer } from '@/features/customers/types'
import { demoPipeline } from '@/features/pipeline/demo/demoPipeline'
import type {
  BookAppointmentInput,
  CreateSaleInput,
  PlanActivityInput,
} from '@/features/actions/schemas/actionSchemas'
import type { LeadIntakeInput } from '@/features/pipeline/schemas/leadIntakeSchema'
import type { CreateLeadInput } from '@/features/pipeline/schemas/leadSchema'
import type { Lead, LeadIntakeResult } from '@/features/pipeline/types'
import type { PipelineState } from '@/types/models'
import { env } from '@/lib/env'
import { useAuthStore } from '@/stores/authStore'
import { splitFullName } from '@/lib/utils/names'

/** Cards per column request; "Weitere laden" asks for the next page of the same size. */
export const COLUMN_PAGE_SIZE = 50

export interface PipelineColumnPage {
  leads: Lead[]
  page: number
  size: number
  total: number
}

interface LeadRequest {
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  origin: string
}

interface LeadResponse {
  customerPublicId: string
  created: boolean
  enrichedFields: string[]
  name: string | null
  email: string
  phone: string | null
  origin: string
  originLabel: string
  pipeline: PipelineState
}

function toLeadRequest(input: LeadIntakeInput): LeadRequest {
  return {
    email: input.email,
    ...splitFullName(input.name),
    phone: input.phone,
    origin: input.origin,
  }
}

function toLeadIntakeResult({ customerPublicId, ...lead }: LeadResponse): LeadIntakeResult {
  return { customerId: customerPublicId, ...lead }
}

/**
 * A customer as the board draws it. This is a re-shaping, not a wire mapping —
 * `customersApi` already translated the response, and a card and a Kunden row
 * are the same record seen two ways.
 */
function toLead(customer: Customer): Lead {
  return {
    id: customer.id,
    customerId: customer.id,
    name: customer.name,
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    stageId: customer.stage ?? '',
    stageLabel: customer.stageLabel,
    stageSection: customer.stageSection,
    originLabel: customer.originLabel,
    attemptCount: customer.attemptCount,
    nextActionAt: customer.nextActionAt ?? null,
    dueToday: customer.dueToday,
  }
}

export const pipelineApi = {
  /**
   * One board column. Each column pages on its own (`GET /api/customers?stage=`),
   * so a crowded "Neu" never delays an empty "Ausgeschieden". The columns
   * themselves come from the session's `stages`, never from the rows.
   */
  fetchColumn: async (stage: string, page: number): Promise<PipelineColumnPage> => {
    if (env.demoMode && !env.complianceDevelopment)
      return demoPipeline.fetchColumn(stage, page, COLUMN_PAGE_SIZE)

    const { items, ...rest } = await customersApi.fetchPage({
      stage,
      page,
      size: COLUMN_PAGE_SIZE,
    })
    return { ...rest, leads: items.map(toLead) }
  },

  createLead: (input: CreateLeadInput) => demoPipeline.createLead(input),

  submitLeadIntake: async (input: LeadIntakeInput): Promise<LeadIntakeResult> => {
    if (env.sharedShow) {
      if (input.studio !== useAuthStore.getState().session?.location?.publicId)
        throw new Error('Bitte wechseln Sie zuerst den Standort.')
      const created = await sharedShowApi.createCustomer(input, input.origin)
      return {
        customerId: created.id,
        created: true,
        enrichedFields: [],
        name: created.name,
        email: created.email,
        phone: created.phone,
        origin: created.origin!,
        originLabel: created.originLabel!,
        pipeline: created.pipeline!,
      }
    }
    if (env.demoMode) {
      const studio = useAuthStore
        .getState()
        .session?.locations.find((location) => location.publicId === input.studio)
      if (!studio) throw new Error('Bitte wählen Sie einen gültigen Standort.')
      return demoPipeline.submitLeadIntake({ ...input, studio: studio.name })
    }

    const response = await api.post<LeadResponse>(endpoints.pipeline.leads, toLeadRequest(input))
    return toLeadIntakeResult(response)
  },

  planActivity: (input: PlanActivityInput) =>
    // NOT ON THE BACKEND YET.
    demoPipeline.planActivity({ ...input, leadId: input.subjectId }),

  /**
   * The appointment endpoint is one endpoint. Booking from here goes through
   * the scheduler's mapper so the payload is the backend's shape — every screen
   * shares one booking form, so they must not each invent a request body.
   */
  bookAppointment: (input: BookAppointmentInput, timezone: string) =>
    env.demoMode && !env.complianceDevelopment
      ? demoPipeline.bookAppointment({ ...input, leadId: input.subjectId })
      : schedulerApi.create(input, timezone),

  createSale: (input: CreateSaleInput) =>
    // NOT ON THE BACKEND YET.
    demoPipeline.createSale({ ...input, leadId: input.subjectId }),

  /*
   * moveLead is gone, not merely unwired. A stage is computed from recorded
   * facts (contract K-06), so there is no endpoint to move a lead by hand and
   * there is not meant to be one — the drag it served only ever rearranged the
   * local cache. Recording the call or the appointment is what moves a card.
   */

  simulateLead: () => demoPipeline.simulateLead(),
}
